import { cloneElement, isValidElement, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";
import styles from "./Dropdown.module.css";
import createSlotProps from "../slotProps";

/**
 * Native field props plus dropdown behavior. `styles` augments and `classes`
 * replaces root, control, field, toggleButton, toggleIcon, and menu slots.
 * Portal positioning is merged before menu style overrides.
 */
export default function Dropdown({
	allowCustom = false,
	children,
	styles: styleOverrides,
	classes,
	renderInPortal = false,
	chevronIcon,
	...inputProps
}) {
	const { value, defaultValue, disabled, readOnly, onChange, onFocus, onKeyDown } = inputProps;
	const [internalValue, setInternalValue] = useState(defaultValue ?? "");
	const currentValue = value ?? internalValue;
	const locked = disabled || readOnly;
	const slotProps = createSlotProps(styleOverrides, classes);
	const [isOpen, setIsOpen] = useState(false);
	const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
	const containerRef = useRef(null);
	const controlRef = useRef(null);
	const menuRef = useRef(null);
	const menuId = useId();

	const hasChildren = Boolean(children);

	const updateMenuPosition = useCallback(() => {
		if (!controlRef.current) {
			return;
		}

		const rect = controlRef.current.getBoundingClientRect();
		setMenuPosition({
			top: rect.bottom + 6,
			left: rect.left,
			width: rect.width,
		});
	}, []);

	useEffect(() => {
		function handleOutsideClick(event) {
			const clickedInsideContainer = containerRef.current?.contains(event.target);
			const clickedInsideMenu = menuRef.current?.contains(event.target);

			if (!clickedInsideContainer && !clickedInsideMenu) {
				setIsOpen(false);
			}
		}

		function handleEscape(event) {
			if (event.key === "Escape") {
				setIsOpen(false);
			}
		}

		document.addEventListener("mousedown", handleOutsideClick);
		document.addEventListener("keydown", handleEscape);

		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
			document.removeEventListener("keydown", handleEscape);
		};
	}, []);

	useEffect(() => {
		if (!renderInPortal || !isOpen || !hasChildren) {
			return;
		}

		updateMenuPosition();
		window.addEventListener("resize", updateMenuPosition);
		window.addEventListener("scroll", updateMenuPosition, true);

		return () => {
			window.removeEventListener("resize", updateMenuPosition);
			window.removeEventListener("scroll", updateMenuPosition, true);
		};
	}, [renderInPortal, isOpen, hasChildren, updateMenuPosition]);

	function handleToggle() {
		if (locked) {
			return;
		}

		setIsOpen((prev) => !prev);
	}

	function handleInputFocus(event) {
		onFocus?.(event);
		if (locked || !hasChildren) {
			return;
		}

		setIsOpen(true);
	}

	function handleInputChange(event) {
		if (value === undefined) {
			setInternalValue(event.target.value);
		}
		onChange?.(event);

		if (locked || !hasChildren) {
			return;
		}

		setIsOpen(true);
	}

	function handleKeyDown(event) {
		onKeyDown?.(event);
		if (event.defaultPrevented || locked || !hasChildren) {
			return;
		}
		if (event.key === "ArrowDown") {
			event.preventDefault();
			setIsOpen(true);
		}
	}

	const renderedChildren = typeof children === "function"
		? children({
			value: currentValue,
			isOpen,
			closeMenu: () => setIsOpen(false),
			openMenu: () => setIsOpen(true),
		})
		: children;

	const shouldShowMenu = isOpen && hasChildren && !locked;
	const iconProps = slotProps("toggleIcon", [
		styles.chevronIcon,
		shouldShowMenu ? styles.chevronOpen : styles.chevronClosed,
	].join(" "));
	const resolvedChevron = isValidElement(chevronIcon)
		? cloneElement(chevronIcon, {
			...iconProps,
			"aria-hidden": true,
		})
		: <FaChevronDown {...iconProps} aria-hidden="true" />;
	const menuElement = shouldShowMenu ? (
		<div
			ref={menuRef}
			data-modal-safe-portal={renderInPortal ? "true" : undefined}
			id={menuId}
			{...slotProps("menu", [
				styles.menu,
				renderInPortal ? styles.menuPortal : "",
			].filter(Boolean).join(" "), renderInPortal ? {
				position: "fixed",
				top: `${menuPosition.top}px`,
				left: `${menuPosition.left}px`,
				width: `${menuPosition.width}px`,
			} : undefined)}
			role="listbox"
		>
			{renderedChildren}
		</div>
	) : null;

	return (
		<div
			ref={containerRef}
			{...slotProps("root", styles.dropdown)}
		>
			<div ref={controlRef} {...slotProps("control", styles.control)}>
				<input
					{...inputProps}
					defaultValue={undefined}
					type="text"
					value={currentValue}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					onKeyDown={handleKeyDown}
					{...slotProps("field", styles.input)}
					readOnly={readOnly || !allowCustom}
					role="combobox"
					aria-expanded={shouldShowMenu}
					aria-haspopup="listbox"
					aria-controls={shouldShowMenu ? menuId : undefined}
				/>
				<button
					type="button"
					{...slotProps("toggleButton", styles.toggleButton)}
					onClick={handleToggle}
					disabled={locked}
					aria-label="Toggle dropdown menu"
				>
					{resolvedChevron}
				</button>
			</div>

			{renderInPortal ? (shouldShowMenu ? createPortal(menuElement, document.body) : null) : menuElement}
		</div>
	);
}
